import { createContext, useContext, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { euro } from '@immoaktas/shared';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';

export type Crumb = { to: string; label: string };
/**
 * Breadcrumb trail the app frame puts in front of every page header (two-level navigation, design/NavKonzept):
 * inside a property "Alle Objekte › Lindenstraße 12 › Belege". `atRoot`: the page is the last trail entry itself,
 * so that entry is the current page (no link) and the page's own title is not repeated.
 */
export type PageTrail = { crumbs: Crumb[]; atRoot: boolean; back?: Crumb | null; /** Controls next to the trail (unit switcher). */ tools?: ReactNode };
const PageTrailContext = createContext<PageTrail | null>(null);
export function PageTrailProvider({ value, children }: { value: PageTrail | null; children: ReactNode }) {
  return <PageTrailContext.Provider value={value}>{children}</PageTrailContext.Provider>;
}
export const usePageTrail = () => useContext(PageTrailContext);

/**
 * Crumbs a header shows: without a trail the page's own crumbs; with a trail the trail, then the page's own crumbs that
 * are not in it yet (the property list "/objekte" is covered by "Alle Objekte"), then the current page (`current`).
 */
export function mergeCrumbs(trail: PageTrail | null, own: Crumb[] = [], current?: string): { links: Crumb[]; current?: string } {
  if (!trail) return { links: own };
  if (trail.atRoot) return { links: trail.crumbs.slice(0, -1), current: trail.crumbs[trail.crumbs.length - 1]?.label };
  const seen = new Set(trail.crumbs.map((c) => c.to));
  const extra = own.filter((c) => c.to !== '/objekte' && !seen.has(c.to));
  return { links: [...trail.crumbs, ...extra], current };
}

/**
 * Page header (all screens): breadcrumbs, eyebrow, h1, sub line, actions on the right. Inside the app frame's trail
 * (PageTrailProvider) the breadcrumbs always start with the level ("Alle Objekte › Objekt"); `crumb` names the
 * current page in the trail when `title` is not plain text. A trail with `back` adds a visible "← Zurück zu <parent>"
 * button (the logical parent, not the browser history) unless the page brings its own back link as `eyebrow`.
 */
export function PageHeader({ title, crumbs, crumb, eyebrow, actions, sub }: { title: ReactNode; crumbs?: Crumb[]; crumb?: string; eyebrow?: ReactNode; actions?: ReactNode; sub?: ReactNode }) {
  const { t } = useTranslation('common');
  const trail = usePageTrail();
  const { links, current } = mergeCrumbs(trail, crumbs, crumb ?? (typeof title === 'string' ? title : undefined));
  const back = !eyebrow && trail?.back ? trail.back : null;
  return (
    <header className="page-head">
      <div className="titles">
        {back && (
          <Link to={back.to} className="head-back">
            <Icon name="arrowLeft" size={16} strokeWidth={2} />
            <span>{t('ui.backTo', { label: back.label })}</span>
          </Link>
        )}
        {(links.length > 0 || current) && (
          <nav className="crumbs" aria-label={t('ui.breadcrumb')}>
            <ol>
              {links.map((c, i) => (
                <li key={c.to + i}>
                  {i > 0 && <span className="sep" aria-hidden>›</span>}
                  <Link to={c.to}>{c.label}</Link>
                </li>
              ))}
              {current && (
                <li>
                  {links.length > 0 && <span className="sep" aria-hidden>›</span>}
                  <span className="current" aria-current="page">{current}</span>
                </li>
              )}
            </ol>
          </nav>
        )}
        {trail?.tools && <div className="head-tools">{trail.tools}</div>}
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}

export type CardTone = 'default' | 'tint' | 'warn' | 'danger' | 'ok';
/** Surface card. `flush` removes padding (tables); the head then gets its own padding and divider. */
/** `level`: heading level of the title (3 by default; 2 for cards that are top-level sections right under the page h1). */
/**
 * `onEdit` makes the whole card a click target for editing (pointer) and adds an edit button to the head
 * (keyboard, screen readers); clicks on links, buttons and inputs inside the card keep their own action.
 */
export function Card({ title, sub, children, actions, className = '', tone = 'default', roomy, as: As = 'section', labelledBy, level = 3, onEdit, editLabel }: { title?: ReactNode; sub?: ReactNode; children?: ReactNode; actions?: ReactNode; className?: string; tone?: CardTone; roomy?: boolean; as?: 'section' | 'div' | 'article' | 'aside'; labelledBy?: string; level?: 2 | 3; onEdit?: () => void; editLabel?: string }) {
  const { t } = useTranslation('common');
  const H = level === 2 ? 'h2' : 'h3';
  const classes = ['card', tone === 'default' ? '' : tone, roomy ? 'roomy' : '', onEdit ? 'editable' : '', className].filter(Boolean).join(' ');
  const onClick = onEdit
    ? (e: MouseEvent<HTMLElement>) => {
        if ((e.target as HTMLElement).closest('a, button, input, select, textarea, label, [role="button"]')) return;
        if (window.getSelection()?.toString()) return; // selecting text is not a click
        onEdit();
      }
    : undefined;
  const editButton = onEdit && (
    <button type="button" className="btn ghost sm card-edit" onClick={onEdit}>
      <Icon name="edit" size={16} strokeWidth={1.8} />
      {editLabel ?? t('ui.edit')}
    </button>
  );
  return (
    <As className={classes} aria-labelledby={labelledBy} onClick={onClick}>
      {(title || actions || editButton) && (
        <div className="card-head">
          <div>
            {title && <H className="card-title" id={labelledBy}>{title}</H>}
            {sub && <p className="card-sub">{sub}</p>}
          </div>
          {(actions || editButton) && <div className="card-actions">{actions}{editButton}</div>}
        </div>
      )}
      {children}
    </As>
  );
}

/** KPI tile (design/Dashboard): label, big number, optional sub line; `to` makes the tile a link. */
/** `filled` tints the whole tile in the tone colour (design/Zahlungen "Offene Posten gesamt"). */
/** `onClick` makes it a button (opens a dialog right there, e.g. "Kaltmiete" → rent change); `actionLabel` is read after the label. */
export function Kpi({ value, label, sub, tone, to, filled, onClick, actionLabel }: { value: ReactNode; label: ReactNode; sub?: ReactNode; tone?: 'danger' | 'ok' | 'warn'; to?: string; filled?: boolean; onClick?: () => void; actionLabel?: string }) {
  const content = (
    <>
      <span className="l">{label}</span>
      <span className="v">{value}</span>
      {sub && <span className="s" title={typeof sub === 'string' ? sub : undefined}>{sub}</span>}
    </>
  );
  const cls = `card kpi ${tone ? `is-${tone}` : ''} ${filled && tone ? 'filled' : ''}`;
  const action = actionLabel ? <span className="sr-only">{actionLabel}</span> : null;
  if (onClick) return <button type="button" className={`${cls} as-button`} onClick={onClick}>{content}{action}</button>;
  return to ? <Link to={to} className={cls}>{content}{action}</Link> : <div className={cls}>{content}</div>;
}

export const KpiGrid = ({ children, label }: { children: ReactNode; label?: string }) => (
  <div className="kpi-grid stagger" role={label ? 'group' : undefined} aria-label={label}>
    {children}
  </div>
);

/** Initials avatar ("TA"), light variant for lists, dark for the sidebar. */
export function Avatar({ name, light, size }: { name: string; light?: boolean; size?: number }) {
  const initials = initialsOf(name);
  return <span className={`avatar ${light ? 'light' : ''}`} style={size ? { width: size, height: size } : undefined} aria-hidden>{initials}</span>;
}
export const initialsOf = (name: string) =>
  name
    .replace(/[^\p{L}\s-]/gu, ' ')
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '·';

/** Badge with text (colour never alone, docs/08). `dot` adds the coloured status dot of the design. */
export type PillTone = 'ok' | 'warn' | 'err' | 'neutral' | 'manual' | 'brand' | 'info' | 'outline';
export const Pill = ({ tone = 'neutral', children, pulse, dot, size }: { tone?: PillTone; children: ReactNode; pulse?: boolean; dot?: boolean; size?: 'sm' | 'md' | 'lg' }) => (
  <span className={['pill', tone, pulse ? 'pulse' : '', size && size !== 'sm' ? size : ''].filter(Boolean).join(' ')}>
    {dot && <span className="pill-dot" aria-hidden />}
    {children}
  </span>
);

/** Euro amount, tabular. `negative` shows the minus sign in danger colour (e.g. arrears). */
export const Money = ({ cents, strong, negative }: { cents: number | null | undefined; strong?: boolean; negative?: boolean }) => (
  <span className={`money ${negative ? 'neg' : ''}`} style={strong ? { fontWeight: 700 } : undefined}>
    {negative && cents ? `−${euro(Math.abs(cents))}` : euro(cents)}
  </span>
);

/** Tabs with sliding underline (320 ms, docs/10). */
export function Tabs<K extends string>({ tabs, value, onChange, label }: { tabs: { key: K; label: string; badge?: ReactNode }[]; value: K; onChange: (k: K) => void; label?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [ink, setInk] = useState<{ x: number; w: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (el) setInk({ x: el.offsetLeft, w: el.offsetWidth });
  }, [value, tabs.length]);
  const move = (dir: 1 | -1) => {
    const i = tabs.findIndex((t) => t.key === value);
    const next = tabs[(i + dir + tabs.length) % tabs.length];
    if (next) {
      onChange(next.key);
      requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus());
    }
  };
  return (
    <div className="tabs" role="tablist" aria-label={label} ref={ref} onKeyDown={(e) => { if (e.key === 'ArrowRight') move(1); else if (e.key === 'ArrowLeft') move(-1); }}>
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={t.key === value} tabIndex={t.key === value ? 0 : -1} className={t.key === value ? 'active' : ''} onClick={() => onChange(t.key)}>
          {t.label} {t.badge}
        </button>
      ))}
      {ink && <span className="tab-ink" aria-hidden style={{ width: ink.w, transform: `translateX(${ink.x}px)` }} />}
    </div>
  );
}

/** Vertical sub navigation (settings). Items are buttons (in-page sections) or links (`to`). */
export function SubNav<K extends string>({ items, value, onChange, label }: { items: { key: K; label: string; to?: string }[]; value: K; onChange?: (k: K) => void; label: string }) {
  return (
    <nav aria-label={label}>
      <ul className="subnav">
        {items.map((i) => (
          <li key={i.key}>
            {i.to ? (
              <Link to={i.to} aria-current={i.key === value ? 'page' : undefined}>{i.label}</Link>
            ) : (
              <button type="button" aria-current={i.key === value ? 'page' : undefined} onClick={() => onChange?.(i.key)}>{i.label}</button>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function ProgressBar({ value, label, tone, thin, indeterminate }: { value?: number; label?: string; tone?: 'ok' | 'warn' | 'err'; thin?: boolean; indeterminate?: boolean }) {
  const pct = Math.max(0, Math.min(100, Math.round((value ?? 0) * 100)));
  return (
    <div className={['progress', tone ?? '', thin ? 'thin' : '', indeterminate ? 'indeterminate' : ''].filter(Boolean).join(' ')} role="progressbar" aria-label={label} aria-valuenow={indeterminate ? undefined : pct} aria-valuemin={0} aria-valuemax={100}>
      <div style={indeterminate ? undefined : { width: `${pct}%` }} />
    </div>
  );
}

/** Step segments ("3 von 4 erledigt"). */
export const StepsBar = ({ total, done, label }: { total: number; done: number; label: string }) => (
  <div className="steps-bar" role="img" aria-label={label}>
    {Array.from({ length: total }, (_, i) => <span key={i} className={i < done ? 'done' : ''} />)}
  </div>
);

/** Horizontal value bar row (design/Dashboard "Kosten je m²"). `ratio` 0..1. */
export const BarRow = ({ label, value, ratio, current }: { label: ReactNode; value: ReactNode; ratio: number; current?: boolean }) => (
  <div className="bar-row">
    <span className="muted">{label}</span>
    <span className="bar-track"><span className={`bar-fill ${current ? 'current' : ''}`} style={{ width: `${Math.max(2, Math.min(100, ratio * 100))}%` }} /></span>
    <span className="bar-value">{value}</span>
  </div>
);

export type BannerTone = 'warn' | 'err' | 'info' | 'ok' | 'lock' | 'plain';
export const Banner = ({ tone = 'info', children, role, icon }: { tone?: BannerTone; children: ReactNode; role?: 'alert' | 'status'; icon?: ReactNode }) => (
  <div className={`banner ${tone}`} role={role}>
    {icon}
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
  </div>
);
